import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";

/* --- Public pages (eager — small) --- */
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import ContactSales from "@/pages/ContactSales";
import Demo from "@/pages/Demo";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Security from "@/pages/Security";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ConfirmEmail from "@/pages/ConfirmEmail";
import AmazonConnectionHelp from "@/pages/AmazonConnectionHelp";

/* --- V2 shell (eager — always needed) --- */
import { V2AppShell } from "@/v2/layout/AppShell";
import { DecisionProvider } from "@/v2/context/DecisionContext";
import LegacyEmbed from "@/v2/pages/LegacyEmbed";
import ComingSoon from "@/v2/pages/ComingSoon";

/* --- V2 pages (lazy) --- */
const V2Dashboard         = lazy(() => import("@/v2/pages/Dashboard"));
const ControlTower        = lazy(() => import("@/v2/pages/ControlTower"));
const PersonalDashboard   = lazy(() => import("@/v2/pages/PersonalDashboard"));
const MyInbox             = lazy(() => import("@/v2/pages/MyInbox"));
const AIDigest            = lazy(() => import("@/v2/pages/AIDigest"));
const ExecutiveBI         = lazy(() => import("@/v2/pages/ExecutiveBI"));
const FinanceCommand      = lazy(() => import("@/v2/pages/FinanceCommand"));
const AdminFinanceMaster  = lazy(() => import("@/v2/pages/AdminFinanceMaster"));
const AdminBillingDashboard = lazy(() => import("@/v2/pages/AdminBillingDashboard"));
const CustomerBillingPortal = lazy(() => import("@/v2/pages/CustomerBillingPortal"));
const AdminLegalCompliance = lazy(() => import("@/v2/pages/AdminLegalCompliance"));
const CustomerLegalPortal = lazy(() => import("@/v2/pages/CustomerLegalPortal"));
const AdminSubscriptions = lazy(() => import("@/v2/pages/AdminSubscriptions"));
const AdminSecurity = lazy(() => import("@/v2/pages/AdminSecurity"));
const AdminDocumentHealth = lazy(() => import("@/v2/pages/AdminDocumentHealth"));
const AdminProductionHealth = lazy(() => import("@/v2/pages/AdminProductionHealth"));
const AdminDeploymentParity = lazy(() => import("@/v2/pages/AdminDeploymentParity"));
const AdminShopifyRca = lazy(() => import("@/v2/pages/AdminShopifyRca"));
const AuthHub = lazy(() => import("@/v2/pages/AuthHub"));
const AdminInviteUsers = lazy(() => import("@/v2/pages/AdminInviteUsers"));
const ProfileSecurity = lazy(() => import("@/v2/pages/ProfileSecurity"));
const WorkflowStudio      = lazy(() => import("@/v2/pages/WorkflowStudio"));
const AgencyCommand       = lazy(() => import("@/v2/pages/AgencyCommand"));
const OrdersWorkspace     = lazy(() => import("@/v2/pages/OrdersWorkspace"));
const InventoryIntelligence = lazy(() => import("@/v2/pages/InventoryIntelligence"));
const ListingCenter       = lazy(() => import("@/v2/pages/ListingCenter"));
const ProductIntelligence = lazy(() => import("@/v2/pages/ProductIntelligence"));
const OnboardingWizard    = lazy(() => import("@/v2/pages/OnboardingWizard"));
const SyncCommand         = lazy(() => import("@/v2/pages/SyncCommand"));
const MarketplaceHub      = lazy(() => import("@/v2/pages/MarketplaceHub"));
const MarketplaceDetail   = lazy(() => import("@/v2/pages/MarketplaceDetail"));
const AmazonOnboardingWizard = lazy(() => import("@/v2/pages/AmazonOnboardingWizard"));
const MarketplacesDashboard = lazy(() => import("@/v2/pages/MarketplacesDashboard"));
const MarketplacesErrorsPage = lazy(() =>
  import("@/v2/pages/MarketplacesDashboard").then((m) => ({ default: m.MarketplaceErrorsCenter }))
);
const AIInsightsCenter    = lazy(() => import("@/v2/pages/AIInsights"));
const ProcurementCenter   = lazy(() => import("@/v2/pages/ProcurementCenter"));
const Customers           = lazy(() => import("@/v2/pages/Customers"));
const CustomerProfile     = lazy(() => import("@/v2/pages/CustomerProfile"));
const OnboardingCustomer  = lazy(() => import("@/v2/pages/OnboardingCustomer"));
const FinanceReports      = lazy(() => import("@/v2/pages/FinanceReports"));
const CustomerPortal      = lazy(() => import("@/v2/pages/CustomerPortal"));
const AdminConsole        = lazy(() => import("@/v2/pages/AdminConsole"));
const ReturnsWorkspace    = lazy(() => import("@/v2/pages/ReturnsWorkspace"));
const ReturnDetail        = lazy(() => import("@/v2/pages/ReturnDetail"));
const DocumentTemplatesPage = lazy(() => import("@/v2/pages/DocumentTemplatesPage"));
const InventorySyncDashboard = lazy(() => import("@/v2/pages/InventorySyncDashboard"));
const TenantCompanyMaster = lazy(() => import("@/v2/pages/TenantCompanyMaster"));

/* --- Legacy pages (lazy) --- */
const Dashboard           = lazy(() => import("@/pages/Dashboard"));
const Orders              = lazy(() => import("@/pages/Orders"));
const OrderDetail         = lazy(() => import("@/pages/OrderDetail"));
const Products            = lazy(() => import("@/pages/Products"));
const Inventory           = lazy(() => import("@/pages/Inventory"));
const Returns             = lazy(() => import("@/pages/Returns"));
const Shipping            = lazy(() => import("@/pages/Shipping"));
const Automation          = lazy(() => import("@/pages/Automation"));
const ShopifyStores       = lazy(() => import("@/pages/ShopifyStores"));
const AIOperations        = lazy(() => import("@/pages/AIOperations"));
const CommandCenter       = lazy(() => import("@/pages/CommandCenter"));
const AIInventoryAssistant = lazy(() => import("@/pages/AIInventoryAssistant"));
const WarehouseOps        = lazy(() => import("@/pages/WarehouseOps"));
const Fulfillment         = lazy(() => import("@/pages/Fulfillment"));
const Couriers            = lazy(() => import("@/pages/Couriers"));
const ConnectorHealth     = lazy(() => import("@/pages/ConnectorHealth"));
const WorkflowBuilder     = lazy(() => import("@/pages/WorkflowBuilder"));
const Forecasting         = lazy(() => import("@/pages/Forecasting"));
const Finance             = lazy(() => import("@/pages/Finance"));
const DeveloperPortal     = lazy(() => import("@/pages/DeveloperPortal"));
const BillingDashboard    = lazy(() => import("@/pages/BillingDashboard"));
const Marketplace         = lazy(() => import("@/pages/Marketplace"));
const Marketplaces        = lazy(() => import("@/pages/Marketplaces"));
const EmailsConsole       = lazy(() => import("@/pages/EmailsConsole"));
const TrainingCenter      = lazy(() => import("@/pages/TrainingCenter"));
const Profile             = lazy(() => import("@/pages/Profile"));
const PrintCenter         = lazy(() => import("@/pages/PrintCenter"));
const DocumentHealth      = lazy(() => import("@/pages/DocumentHealth"));
const MarketplaceTemplates = lazy(() => import("@/pages/MarketplaceTemplates"));
const MyntraDashboard     = lazy(() => import("@/pages/MyntraDashboard"));
const Agencies            = lazy(() => import("@/pages/Agencies"));
const Clients             = lazy(() => import("@/pages/Clients"));
const Brands              = lazy(() => import("@/pages/Brands"));
const Warehouses          = lazy(() => import("@/pages/Warehouses"));
const Tenants             = lazy(() => import("@/pages/Tenants"));
const Plans               = lazy(() => import("@/pages/Plans"));
const AuditLogs           = lazy(() => import("@/pages/AuditLogs"));
const Users               = lazy(() => import("@/pages/Users"));
const AIInsightsLegacy    = lazy(() => import("@/pages/AIInsights"));
const Settings            = lazy(() => import("@/pages/Settings"));
const ClientWorkspaceLayout = lazy(() => import("@/pages/workspace/ClientWorkspaceLayout"));
const WorkspaceDashboard  = lazy(() => import("@/pages/workspace/WorkspaceDashboard"));

/* Named-export workspace sections: build tiny loader wrappers */
const WorkspaceOrders    = lazy(() => import("@/pages/workspace/WorkspaceSections").then((m) => ({ default: m.WorkspaceOrders })));
const WorkspaceProducts  = lazy(() => import("@/pages/workspace/WorkspaceSections").then((m) => ({ default: m.WorkspaceProducts })));
const WorkspaceInventory = lazy(() => import("@/pages/workspace/WorkspaceSections").then((m) => ({ default: m.WorkspaceInventory })));
const WorkspaceFulfilment = lazy(() => import("@/pages/workspace/WorkspaceLiveSections").then((m) => ({ default: m.WorkspaceFulfilment })));
const WorkspaceReturns    = lazy(() => import("@/pages/workspace/WorkspaceLiveSections").then((m) => ({ default: m.WorkspaceReturns })));
const WorkspaceFinance    = lazy(() => import("@/pages/workspace/WorkspaceLiveSections").then((m) => ({ default: m.WorkspaceFinance })));
const WorkspaceAutomation = lazy(() => import("@/pages/workspace/WorkspaceLiveSections").then((m) => ({ default: m.WorkspaceAutomation })));
const WorkspaceReports    = lazy(() => import("@/pages/workspace/WorkspaceLiveSections").then((m) => ({ default: m.WorkspaceReports })));
const WorkspaceSettings   = lazy(() => import("@/pages/workspace/WorkspaceLiveSections").then((m) => ({ default: m.WorkspaceSettings })));

function ThemeBoot() {
  useEffect(() => {
    const dark = localStorage.getItem("zj_theme") !== "light";
    document.documentElement.classList.toggle("dark", dark);
  }, []);
  return null;
}

function RootGate() {
  const { user } = useAuth();
  if (user === null) {
    return <div style={{ height: "100vh", display: "grid", placeItems: "center", fontFamily: "monospace", fontSize: 13 }}>Loading…</div>;
  }
  if (user) {
    if (user.role === "customer_admin") return <Navigate to="/portal" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <Landing />;
}

const Fallback = () => (
  <div style={{ display: "grid", placeItems: "center", minHeight: "40vh", padding: 40 }}>
    <div className="v2-skeleton" style={{ width: 220, height: 60, borderRadius: 8 }} />
  </div>
);

const S = (El) => <Suspense fallback={<Fallback />}>{El}</Suspense>;
const Wrap = (El) => <LegacyEmbed>{El}</LegacyEmbed>;

/* V2 shell provider — wraps shell with DecisionProvider so recommendations
   are available on every V2 page + in AI Assistant. */
function V2Shell() {
  return (
    <DecisionProvider>
      <ErrorBoundary>
        <V2AppShell />
      </ErrorBoundary>
    </DecisionProvider>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeBoot />
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <ErrorBoundary>
          <Routes>
            {/* Public */}
            <Route path="/" element={<RootGate />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact-sales" element={<ContactSales />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/security" element={<Security />} />
            <Route path="/help/amazon-connection" element={<AmazonConnectionHelp />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth" element={S(<AuthHub />)} />
            <Route path="/accept-invite" element={S(<AuthHub />)} />
            <Route path="/verify-email" element={S(<AuthHub />)} />
            <Route path="/reset-password" element={S(<AuthHub />)} />
            <Route path="/reset" element={S(<AuthHub />)} />
            <Route path="/forgot" element={S(<AuthHub />)} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />

            {/* V2 (DEFAULT) protected shell — DecisionProvider wraps every page */}
            <Route element={<ProtectedRoute><V2Shell /></ProtectedRoute>}>
              <Route path="/dashboard" element={S(<ControlTower />)} />
              <Route path="/dashboard-classic" element={S(<V2Dashboard />)} />
              <Route path="/my-dashboard" element={S(<PersonalDashboard />)} />
              <Route path="/inbox" element={S(<MyInbox />)} />
              <Route path="/digest" element={S(<AIDigest />)} />
              <Route path="/bookmarks" element={<Navigate to="/inbox?tab=bookmarks" replace />} />

              {/* Phase G — AI Decision Intelligence + Procurement */}
              <Route path="/insights" element={S(<AIInsightsCenter />)} />
              <Route path="/procurement" element={S(<ProcurementCenter />)} />

              {/* Phase H1 — Platform Administration & Customer Management (super_admin) */}
              <Route path="/customers" element={S(<Customers />)} />
              <Route path="/customers/new" element={S(<OnboardingCustomer />)} />
              <Route path="/customers/:id" element={S(<CustomerProfile />)} />
              <Route path="/finance/reports" element={S(<FinanceReports />)} />
              <Route path="/admin/finance/company-master" element={S(<AdminFinanceMaster />)} />
              <Route path="/settings/company-master" element={S(<TenantCompanyMaster />)} />
              <Route path="/admin/billing" element={S(<AdminBillingDashboard />)} />
              <Route path="/portal/billing" element={S(<CustomerBillingPortal />)} />
              <Route path="/customers/:id/billing" element={S(<CustomerBillingPortal />)} />
              <Route path="/admin/legal" element={S(<AdminLegalCompliance />)} />
              <Route path="/portal/legal" element={S(<CustomerLegalPortal />)} />
              <Route path="/admin/subscriptions" element={S(<AdminSubscriptions />)} />
              <Route path="/admin/security" element={S(<AdminSecurity />)} />
              <Route path="/admin/users/invite" element={S(<AdminInviteUsers />)} />
              <Route path="/profile/security" element={S(<ProfileSecurity />)} />
              <Route path="/portal" element={S(<CustomerPortal />)} />
              <Route path="/admin/console" element={S(<AdminConsole />)} />
              <Route path="/admin/document-health" element={S(<AdminDocumentHealth />)} />
              <Route path="/admin/production-health" element={S(<AdminProductionHealth />)} />
              <Route path="/admin/deployment-parity" element={S(<AdminDeploymentParity />)} />
              <Route path="/admin/shopify-rca" element={S(<AdminShopifyRca />)} />
              <Route path="/admin/audit" element={S(<AdminConsole />)} />
              <Route path="/admin/system" element={S(<AdminConsole />)} />
              <Route path="/admin/support-sessions" element={S(<AdminConsole />)} />

              <Route path="/listings" element={S(<ListingCenter />)} />
              <Route path="/marketplaces" element={S(<MarketplacesDashboard />)} />
              <Route path="/marketplaces/errors" element={S(<MarketplacesErrorsPage />)} />
              <Route path="/marketplace-hub" element={S(<MarketplaceHub />)} />
              <Route path="/marketplace-hub/amazon/wizard" element={S(<AmazonOnboardingWizard />)} />
              <Route path="/marketplace-hub/:key" element={S(<MarketplaceDetail />)} />
              <Route path="/reports" element={<ComingSoon title="Reports" description="Executive, operations, and financial reports with scheduled exports and PDFs." legacyPath="/ai-ops" eta="Phase C" />} />
              <Route path="/bi" element={S(<ExecutiveBI />)} />
              <Route path="/finance" element={S(<FinanceCommand />)} />
              <Route path="/documents" element={<ComingSoon title="Documents" description="Contracts, GST filings, invoices, and MSAs stored per tenant." eta="Phase E" />} />
              <Route path="/support" element={<ComingSoon title="Support" description="Live chat, tickets, and knowledge-base search — powered by Jini AI." legacyPath="/training" eta="Phase E" />} />

              <Route path="/orders" element={S(<OrdersWorkspace />)} />
              <Route path="/orders/:id" element={S(Wrap(<OrderDetail />))} />
              <Route path="/products" element={S(<ProductIntelligence />)} />
              <Route path="/inventory" element={S(<InventoryIntelligence />)} />
              <Route path="/returns" element={S(<ReturnsWorkspace />)} />
              <Route path="/returns/:id" element={S(<ReturnDetail />)} />
              <Route path="/returns/legacy" element={S(Wrap(<Returns />))} />
              <Route path="/admin/document-templates" element={S(<DocumentTemplatesPage />)} />
              <Route path="/inventory-sync" element={S(<InventorySyncDashboard />)} />
              <Route path="/shipping" element={S(Wrap(<Shipping />))} />
              <Route path="/automation" element={S(<WorkflowStudio />)} />
              <Route path="/agency" element={S(<AgencyCommand />)} />
              <Route path="/shopify" element={S(Wrap(<ShopifyStores />))} />
              <Route path="/ai-ops" element={S(Wrap(<AIOperations />))} />
              <Route path="/command" element={S(Wrap(<CommandCenter />))} />
              <Route path="/ai-inventory" element={S(Wrap(<AIInventoryAssistant />))} />
              <Route path="/wms" element={S(Wrap(<WarehouseOps />))} />
              <Route path="/fulfillment" element={S(Wrap(<Fulfillment />))} />
              <Route path="/couriers" element={S(Wrap(<Couriers />))} />
              <Route path="/connector-health" element={S(<SyncCommand />)} />
              <Route path="/onboarding" element={S(<OnboardingWizard />)} />
              <Route path="/workflow-builder" element={S(Wrap(<WorkflowBuilder />))} />
              <Route path="/forecasting" element={S(Wrap(<Forecasting />))} />
              <Route path="/finance-legacy" element={S(Wrap(<Finance />))} />
              <Route path="/developer" element={S(Wrap(<DeveloperPortal />))} />
              <Route path="/billing" element={S(Wrap(<BillingDashboard />))} />
              <Route path="/marketplace" element={S(Wrap(<Marketplace />))} />
              <Route path="/marketplaces" element={S(Wrap(<Marketplaces />))} />
              <Route path="/emails" element={S(Wrap(<EmailsConsole />))} />
              <Route path="/training" element={S(Wrap(<TrainingCenter />))} />
              <Route path="/profile" element={S(Wrap(<Profile />))} />
              <Route path="/print-center" element={S(Wrap(<PrintCenter />))} />
              <Route path="/admin/document-health" element={S(Wrap(<DocumentHealth />))} />
              <Route path="/admin/marketplace-templates" element={S(Wrap(<MarketplaceTemplates />))} />
              <Route path="/integrations/myntra" element={S(Wrap(<MyntraDashboard />))} />
              <Route path="/agencies" element={S(Wrap(<Agencies />))} />
              <Route path="/clients" element={S(Wrap(<Clients />))} />
              <Route path="/brands" element={S(Wrap(<Brands />))} />
              <Route path="/warehouses" element={S(Wrap(<Warehouses />))} />
              <Route path="/tenants" element={S(Wrap(<Tenants />))} />
              <Route path="/plans" element={S(Wrap(<Plans />))} />
              <Route path="/audit" element={S(Wrap(<AuditLogs />))} />
              <Route path="/users" element={S(Wrap(<Users />))} />
              <Route path="/ai" element={S(Wrap(<AIInsightsLegacy />))} />
              <Route path="/settings" element={S(Wrap(<Settings />))} />

              <Route path="/workspace/:clientId" element={S(<ClientWorkspaceLayout />)}>
                <Route index element={S(<WorkspaceDashboard />)} />
                <Route path="orders" element={S(<WorkspaceOrders />)} />
                <Route path="products" element={S(<WorkspaceProducts />)} />
                <Route path="inventory" element={S(<WorkspaceInventory />)} />
                <Route path="fulfilment" element={S(<WorkspaceFulfilment />)} />
                <Route path="returns" element={S(<WorkspaceReturns />)} />
                <Route path="finance" element={S(<WorkspaceFinance />)} />
                <Route path="automation" element={S(<WorkspaceAutomation />)} />
                <Route path="reports" element={S(<WorkspaceReports />)} />
                <Route path="settings" element={S(<WorkspaceSettings />)} />
              </Route>

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>

            {/* LEGACY shell — kept for fallback until V2 reaches feature parity */}
            <Route path="/legacy" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/legacy/dashboard" replace />} />
              <Route path="dashboard" element={S(<Dashboard />)} />
              <Route path="orders" element={S(<Orders />)} />
              <Route path="orders/:id" element={S(<OrderDetail />)} />
              <Route path="products" element={S(<Products />)} />
              <Route path="inventory" element={S(<Inventory />)} />
              <Route path="returns" element={S(<Returns />)} />
              <Route path="shipping" element={S(<Shipping />)} />
              <Route path="automation" element={S(<Automation />)} />
              <Route path="shopify" element={S(<ShopifyStores />)} />
              <Route path="ai-ops" element={S(<AIOperations />)} />
              <Route path="command" element={S(<CommandCenter />)} />
              <Route path="ai-inventory" element={S(<AIInventoryAssistant />)} />
              <Route path="wms" element={S(<WarehouseOps />)} />
              <Route path="fulfillment" element={S(<Fulfillment />)} />
              <Route path="couriers" element={S(<Couriers />)} />
              <Route path="connector-health" element={S(<ConnectorHealth />)} />
              <Route path="workflow-builder" element={S(<WorkflowBuilder />)} />
              <Route path="forecasting" element={S(<Forecasting />)} />
              <Route path="finance" element={S(<Finance />)} />
              <Route path="developer" element={S(<DeveloperPortal />)} />
              <Route path="billing" element={S(<BillingDashboard />)} />
              <Route path="marketplace" element={S(<Marketplace />)} />
              <Route path="marketplaces" element={S(<Marketplaces />)} />
              <Route path="emails" element={S(<EmailsConsole />)} />
              <Route path="training" element={S(<TrainingCenter />)} />
              <Route path="profile" element={S(<Profile />)} />
              <Route path="agencies" element={S(<Agencies />)} />
              <Route path="clients" element={S(<Clients />)} />
              <Route path="brands" element={S(<Brands />)} />
              <Route path="warehouses" element={S(<Warehouses />)} />
              <Route path="tenants" element={S(<Tenants />)} />
              <Route path="plans" element={S(<Plans />)} />
              <Route path="audit" element={S(<AuditLogs />)} />
              <Route path="users" element={S(<Users />)} />
              <Route path="ai" element={S(<AIInsightsLegacy />)} />
              <Route path="settings" element={S(<Settings />)} />
            </Route>
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
